/**
 * Fires a callback after a period of user inactivity, then re-arms so the action
 * repeats for as long as the user stays idle. Any pointer, key, or wheel activity
 * resets the timer. Used to send a comet drifting across an untouched screen.
 */
export class IdleWatcher {
  private readonly timeoutMilliseconds: number
  private readonly onIdle: () => void
  private timerId: number | null

  /**
   * @param {number} timeoutSeconds - Seconds of inactivity before firing.
   * @param {() => void} onIdle - The action to run when idle.
   */
  public constructor(timeoutSeconds: number, onIdle: () => void) {
    this.timeoutMilliseconds = timeoutSeconds * 1000
    this.onIdle = onIdle
    this.timerId = null

    this.handleActivity = this.handleActivity.bind(this)
    window.addEventListener('pointermove', this.handleActivity)
    window.addEventListener('pointerdown', this.handleActivity)
    window.addEventListener('keydown', this.handleActivity)
    window.addEventListener('wheel', this.handleActivity)

    this.arm()
  }

  /**
   * Stop watching and clear any listeners and timers.
   *
   * @returns {void}
   */
  public dispose(): void {
    window.removeEventListener('pointermove', this.handleActivity)
    window.removeEventListener('pointerdown', this.handleActivity)
    window.removeEventListener('keydown', this.handleActivity)
    window.removeEventListener('wheel', this.handleActivity)
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId)
    }
  }

  /**
   * Reset the idle timer in response to user activity.
   *
   * @returns {void}
   */
  private handleActivity(): void {
    this.arm()
  }

  /**
   * (Re)start the idle countdown.
   *
   * @returns {void}
   */
  private arm(): void {
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId)
    }
    this.timerId = window.setTimeout(() => {
      this.onIdle()
      this.arm()
    }, this.timeoutMilliseconds)
  }
}
